import React, { useState } from 'react';
import { TabProps } from './types';
import { getWordStyles, getDifficultyBadge } from './utils';
import WordPlayButton from './WordPlayButton';

const OverviewTab: React.FC<TabProps> = ({
  analyzedWords,
  azureAnalysis,
  transcriptionText,
  getWordPhonemesDisplay,
  phonemesLoading,
}) => {
  const [selectedWord, setSelectedWord] = useState<number | null>(null);

  return (
    <div>
      {/* Word by word with click to expand */}
      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Click a word for detailed analysis
      </h4>
      <div className="flex flex-wrap gap-2 mb-6">
        {analyzedWords.map((item, index) => (
          <div
            key={index}
            className={`
              flex items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all
              ${getWordStyles(item.status)}
              ${selectedWord === index ? 'ring-2 ring-purple-400 ring-offset-2' : ''}
            `}
          >
            <WordPlayButton word={item.word} size="sm" />
            <button
              onClick={() => setSelectedWord(selectedWord === index ? null : index)}
              className="font-medium cursor-pointer"
            >
              {item.word}
            {(item.status === 'correct' || item.status === 'unclear') && (
              <span className="ml-2 text-xs opacity-70">
                {Math.round(item.confidence * 100)}%
              </span>
            )}
            {item.status === 'substitution' && item.transcribed && (
              <span className="ml-2 text-xs opacity-70">
                → {item.transcribed}
              </span>
            )}
            {item.status === 'omission' && (
              <span className="ml-2 text-xs opacity-70">(skipped)</span>
            )}
            {item.status === 'insertion' && (
              <span className="ml-2 text-xs opacity-70">(extra)</span>
            )}
            </button>
          </div>
        ))}
      </div>

      {/* Selected word detail */}
      {selectedWord !== null && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-lg font-bold text-gray-800">
                  "{analyzedWords[selectedWord].word}"
                </h4>
                <WordPlayButton word={analyzedWords[selectedWord].word} size="md" />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 text-xs rounded ${getDifficultyBadge(analyzedWords[selectedWord].difficulty)}`}>
                  {analyzedWords[selectedWord].difficulty} difficulty
                </span>
                {(analyzedWords[selectedWord].status === 'correct' || analyzedWords[selectedWord].status === 'unclear') && (
                  <span className="text-sm text-gray-500">
                    Confidence: {Math.round(analyzedWords[selectedWord].confidence * 100)}%
                  </span>
                )}
              </div>
            </div>
            {analyzedWords[selectedWord].status === 'substitution' &&
             analyzedWords[selectedWord].transcribed && (
              <div className="text-right">
                <div className="text-xs text-gray-500">Heard as:</div>
                <div className="text-red-600 font-medium">
                  "{analyzedWords[selectedWord].transcribed}"
                </div>
              </div>
            )}
            {analyzedWords[selectedWord].status === 'omission' && (
              <div className="text-right">
                <span className="px-2 py-1 bg-red-100 text-red-700 text-sm rounded">
                  Word was skipped
                </span>
              </div>
            )}
            {analyzedWords[selectedWord].status === 'insertion' && (
              <div className="text-right">
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded">
                  Extra word spoken
                </span>
              </div>
            )}
          </div>

          {/* Phoneme breakdown */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-gray-600 mb-2">
              Phoneme Breakdown:
              {phonemesLoading && (
                <span className="ml-2 text-xs text-purple-500 animate-pulse">Loading enhanced phonemes...</span>
              )}
            </h5>
            <div className="flex flex-wrap gap-2">
              {getWordPhonemesDisplay(analyzedWords[selectedWord].word).map((p, i) => (
                <div key={i} className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-mono font-bold text-purple-600">{p.phoneme}</span>
                    <span className="text-gray-400">{p.ipa}</span>
                  </div>
                  <div className={`text-xs mt-1 ${getDifficultyBadge(p.difficulty)} px-1 rounded`}>
                    {p.difficulty}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          {analyzedWords[selectedWord].tips.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-600 mb-2">Tips:</h5>
              <div className="space-y-2">
                {analyzedWords[selectedWord].tips.map((tip, i) => (
                  <div
                    key={i}
                    className={`
                      p-2 rounded text-sm
                      ${tip.type === 'error' ? 'bg-red-50 text-red-700' :
                        tip.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                        tip.type === 'tip' ? 'bg-blue-50 text-blue-700' :
                        'bg-gray-50 text-gray-700'}
                    `}
                  >
                    {tip.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Azure Tips */}
      {azureAnalysis && azureAnalysis.tips && azureAnalysis.tips.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-100">
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Pronunciation Tips
          </h4>
          <ul className="space-y-2">
            {azureAnalysis.tips.map((tip, index) => (
              <li key={index} className="flex items-baseline gap-2 text-blue-700">
                <span className="text-blue-500 font-medium min-w-[20px] text-right">{index + 1}.</span>
                <span className="flex-1">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What we heard */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-500 mb-1">Full transcription:</p>
        <p className="text-gray-700 italic">"{transcriptionText}"</p>
      </div>
    </div>
  );
};

export default OverviewTab;
